<script setup lang="ts">
import TheModal from '@/components/organisms/TheModal/TheModal.vue'
import { useUserNewsModal } from '@/composables/useUserNewsModal'
import userNewsData from '@/data/user-news.json'
import { computed, ref, watch } from 'vue'

const appVersion = (window as any).__APP_VERSION__ || ''

type UserNewsSection = {
    title: string
    summary?: string
    entries?: string[]
}

type UserNewsItem = {
    date: string
    sections: UserNewsSection[]
}

const STORAGE_KEY = 'user-news-last-seen'

const modalRef = ref<InstanceType<typeof TheModal> | null>(null)
const { userNewsModalRequestId } = useUserNewsModal()

const lastSeenDate = ref<Date | null>(loadLastSeenDate())

function loadLastSeenDate(): Date | null {
    try {
        const stored = localStorage.getItem(STORAGE_KEY)
        if (!stored) return null
        const ts = Number(stored)
        return Number.isNaN(ts) ? null : new Date(ts)
    } catch {
        return null
    }
}

function saveLastSeenDate() {
    const newest = newsItems.value[0]
    if (!newest) return
    const newestDate = parseDate(newest.date)
    try {
        localStorage.setItem(STORAGE_KEY, String(newestDate.getTime()))
    } catch { /* quota exceeded — ignore */ }
    lastSeenDate.value = newestDate
}

const newsItems = computed<UserNewsItem[]>(() => {
    const items = ((userNewsData.items ?? []) as Array<Partial<UserNewsItem> & {
        date: string
        title?: string
        summary?: string
        entries?: string[]
        noteTitle?: string
        note?: string
    }>).map((item) => ({
        date: item.date,
        sections: normalizeSections(item),
    }))

    return [...items].sort((left, right) => parseDate(right.date).getTime() - parseDate(left.date).getTime())
})

const unreadSectionCount = computed(() => {
    if (!lastSeenDate.value) {
        return newsItems.value.reduce((sum, item) => sum + item.sections.length, 0)
    }
    return newsItems.value
        .filter((item) => parseDate(item.date).getTime() > lastSeenDate.value!.getTime())
        .reduce((sum, item) => sum + item.sections.length, 0)
})

const hasUnread = computed(() => unreadSectionCount.value > 0)
const hasNews = computed(() => newsItems.value.length > 0)

function parseDate(dateValue: string) {
    const [day, month, year] = dateValue.split('-').map((part) => Number.parseInt(part, 10))

    if (!day || !month || !year) {
        return new Date(dateValue)
    }

    return new Date(year, month - 1, day)
}

function normalizeSections(item: Partial<UserNewsItem> & {
    title?: string
    summary?: string
    entries?: string[]
    noteTitle?: string
    note?: string
}) {
    if (Array.isArray(item.sections) && item.sections.length > 0) {
        return item.sections
    }

    const sections: UserNewsSection[] = []

    if (item.title) {
        sections.push({
            title: item.title,
            summary: item.summary,
            entries: item.entries,
        })
    }

    if (item.noteTitle || item.note) {
        sections.push({
            title: item.noteTitle ?? 'Bemærk',
            summary: item.note,
        })
    }

    return sections
}

function openModal() {
    modalRef.value?.revealDialog()
    saveLastSeenDate()
}

function closeModal() {
    modalRef.value?.closeDialogWithoutEmit()
}

watch(userNewsModalRequestId, (requestId) => {
    if (requestId === 0) {
        return
    }

    openModal()
})
</script>

<template>
    <div v-if="hasNews" class="user-news-bell">
        <button
            type="button"
            class="user-news-bell__button"
            :class="{ 'user-news-bell__button--attention': hasUnread }"
            aria-label="Se nyt i turmodulet"
            title="Se nyt i turmodulet"
            @click="openModal">
            <svg class="user-news-bell__icon" viewBox="0 0 24 24" aria-hidden="true">
                <path
                    d="M12 3.75a4.5 4.5 0 0 0-4.5 4.5v2.2c0 .8-.24 1.58-.68 2.24L5.1 15.25c-.42.64.03 1.5.8 1.5h12.2c.77 0 1.22-.86.8-1.5l-1.72-2.56a4.03 4.03 0 0 1-.68-2.24v-2.2a4.5 4.5 0 0 0-4.5-4.5Z"
                    fill="none"
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.8" />
                <path
                    d="M9.75 18.25a2.25 2.25 0 0 0 4.5 0"
                    fill="none"
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.8" />
            </svg>
            <span v-if="hasUnread" class="user-news-bell__badge">{{ unreadSectionCount }}</span>
            <span class="user-news-bell__sr-only">Se nyt i turmodulet</span>
        </button>

        <TheModal
            ref="modalRef"
            maxWidth="960px"
            width="min(960px, calc(100vw - 32px))">
            <template #body>
                <div class="user-news-modal">
                    <div class="user-news-modal__eyebrow">Nyt for dig</div>
                    <h2 class="user-news-modal__title">Det er nyt i turmodulet</h2>
                    <p class="user-news-modal__lead">
                        Her kan du se de seneste forbedringer, som er relevante for dig som bruger.
                    </p>

                    <div class="user-news-modal__content">
                        <div class="user-news-modal__items">
                            <article
                                v-for="item in newsItems"
                                :key="item.date"
                                class="user-news-card">
                                <div class="user-news-card__date">{{ item.date }}</div>
                                <section
                                    v-for="section in item.sections"
                                    :key="`${item.date}-${section.title}`"
                                    class="user-news-card__section">
                                    <h3 class="user-news-card__section-title">{{ section.title }}</h3>
                                    <p v-if="section.summary" class="user-news-card__summary">{{ section.summary }}</p>
                                    <ul v-if="section.entries?.length" class="user-news-card__list">
                                        <li v-for="entry in section.entries" :key="entry">{{ entry }}</li>
                                    </ul>
                                </section>
                            </article>
                        </div>
                    </div>

                    <div class="user-news-modal__footer">
                        <button class="btn btn-primary" @click="closeModal">Luk</button>
                        <div v-if="appVersion" class="user-news-modal__version">Version {{ appVersion }}</div>
                    </div>
                </div>
            </template>
        </TheModal>
    </div>
</template>

<style lang="scss" scoped>
.user-news-bell {
    color: inherit;

    &__button {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: rem(48);
        height: rem(48);
        border: 1px solid currentColor;
        border-radius: 999px;
        background: transparent;
        color: inherit;
        cursor: pointer;
        transition: transform .2s ease, background-color .2s ease, opacity .2s ease;

        &:hover {
            transform: translateY(-1px);
            background-color: rgba(255, 255, 255, 0.08);
        }

        &--attention {
            animation: user-news-bell-nudge 2.8s ease-in-out infinite;
        }
    }

    &__icon {
        width: rem(22);
        height: rem(22);
    }

    &__badge {
        position: absolute;
        top: rem(-4);
        right: rem(-4);
        min-width: rem(22);
        height: rem(22);
        padding: 0 rem(6);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 999px;
        background-color: #d65245;
        color: $dn-white;
        font-size: rem(12);
        font-weight: 700;
        line-height: 1;
    }

    &__sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
    }
}

.user-news-modal {
    padding: rem(20) rem(8);

    @include media-breakpoint-up(md) {
        padding: rem(28) rem(20);
    }

    &__eyebrow {
        display: inline-flex;
        align-items: center;
        padding: rem(6) rem(12);
        border-radius: 999px;
        background-color: rgba($dn-green, 0.1);
        color: $dn-green;
        font-size: rem(13);
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
    }

    &__title {
        margin: rem(18) 0 rem(12);
        color: $dn-green;
        font-size: rem(30);
        font-weight: 700;
        line-height: 1.1;
    }

    &__lead {
        max-width: rem(720);
        margin-bottom: rem(28);
        color: $gray-700;
        font-size: rem(18);
        line-height: 1.6;
    }

    &__items {
        display: grid;
        gap: rem(18);
    }

    &__content {
        max-height: min(62vh, rem(620));
        overflow-y: auto;
        padding-right: rem(8);
    }

    &__footer {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: rem(12);
        margin-top: rem(32);
    }

    &__version {
        font-size: rem(12);
        color: $gray-500;
        font-weight: 500;
    }
}

.user-news-card {
    padding: rem(22);
    border: 1px solid rgba($dn-green, 0.15);
    border-radius: rem(18);
    background: linear-gradient(180deg, rgba($dn-white, 1) 0%, rgba($dn-green, 0.03) 100%);

    &__section + &__section {
        margin-top: rem(18);
    }

    &__date {
        display: inline-block;
        margin-bottom: rem(12);
        color: $gray-600;
        font-size: rem(13);
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
    }

    &__section {
        padding: 0;
    }

    &__section-title {
        margin-bottom: rem(10);
        color: $dn-green;
        font-size: rem(24);
        font-weight: 700;
        line-height: 1.2;
    }

    &__summary {
        margin-bottom: rem(14);
        color: $gray-700;
        font-size: rem(17);
        line-height: 1.6;
    }

    &__list {
        margin: 0;
        padding-left: rem(22);
        color: $gray-700;

        li + li {
            margin-top: rem(8);
        }
    }
}

@keyframes user-news-bell-nudge {
    0%,
    68%,
    100% {
        transform: translateY(0) rotate(0deg);
    }

    74% {
        transform: translateY(-3px) rotate(-8deg);
    }

    80% {
        transform: translateY(0) rotate(8deg);
    }

    86% {
        transform: translateY(-2px) rotate(-4deg);
    }
}
</style>